import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Download, X } from 'lucide-react';
import PushNotificationPrompt from './PushNotificationPrompt';
import { toast } from 'sonner';

export const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showPushPrompt, setShowPushPrompt] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      
      // Check if we should show push notification prompt after PWA install
      const pushEnabled = localStorage.getItem('push-notifications-enabled');
      const pushSkipped = localStorage.getItem('push-prompt-skipped');
      const justInstalled = localStorage.getItem('pwa-just-installed');
      
      if (justInstalled && !pushEnabled && !pushSkipped && 'Notification' in window && Notification.permission === 'default') {
        // Show push notification prompt after PWA installation
        setTimeout(() => {
          setShowPushPrompt(true);
          localStorage.removeItem('pwa-just-installed');
        }, 1500);
      }
      return;
    }

    const dismissed = localStorage.getItem('pwa-prompt-dismissed');
    if (dismissed) {
      const dismissedDate = new Date(dismissed);
      const daysSinceDismissed = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 7) {
        return;
      }
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Expose globally so other components (e.g., Get App link) can trigger install
      window.__pwaInstallPrompt = e;
      setShowPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setShowPrompt(false);
      setDeferredPrompt(null);
      
      // Mark as just installed for push notification prompt
      localStorage.setItem('pwa-just-installed', 'true');
      
      // Show push notification prompt after installation (slight delay for better UX)
      const pushEnabled = localStorage.getItem('push-notifications-enabled');
      const pushSkipped = localStorage.getItem('push-prompt-skipped');
      
      if (!pushEnabled && !pushSkipped && 'Notification' in window && Notification.permission === 'default') {
        setTimeout(() => {
          setShowPushPrompt(true);
        }, 1500);
      } else {
        toast.success('Rekrutor installed successfully!');
      }
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setIsInstalled(true);
      localStorage.setItem('pwa-just-installed', 'true');
    }
    
    setShowPrompt(false);
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', new Date().toISOString());
  };

  const handlePushEnabled = () => {
    setShowPushPrompt(false);
    toast.success('Notifications enabled! You\'ll stay updated on job matches and messages.');
  };

  const handlePushClosed = () => {
    setShowPushPrompt(false);
  };

  return (
    <>
      {/* Push Notification Prompt - shown after PWA installation */}
      {showPushPrompt && (
        <PushNotificationPrompt 
          onClose={handlePushClosed}
          onEnabled={handlePushEnabled}
        />
      )}

      {/* PWA Install Prompt */}
      {!isInstalled && showPrompt && (
        <div 
          data-testid="pwa-install-prompt"
          className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:w-80 bg-slate-900 border border-slate-700 rounded-lg shadow-xl p-4 z-50 animate-in slide-in-from-bottom-5"
        >
          <button 
            onClick={handleDismiss}
            className="absolute top-2 right-2 text-slate-400 hover:text-white"
            data-testid="pwa-dismiss-btn"
          >
            <X className="w-4 h-4" />
          </button>
          
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-xl">R</span>
            </div>
            
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-white text-sm">Install Rekrutor</h3>
              <p className="text-slate-400 text-xs mt-1">
                Install our app for faster access and offline support
              </p>
            </div>
          </div>
          
          <div className="flex gap-2 mt-4">
            <Button
              onClick={handleDismiss}
              variant="ghost"
              size="sm"
              className="flex-1 text-slate-400 hover:text-white"
              data-testid="pwa-not-now-btn"
            >
              Not now
            </Button>
            <Button
              onClick={handleInstall}
              size="sm"
              className="flex-1 bg-blue-600 hover:bg-blue-700"
              data-testid="pwa-install-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Install
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default PWAInstallPrompt;
